import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';

export const NAV_WIDTH_TRANSITION_DURATION_MS = 400;
export const COLLAPSED_NAV_WIDTH_PX = 56;
export const EXPANDED_NAV_WIDTH_PX = 220;
export const NAV_HOVER_CONTENT_SHIFT_PX = EXPANDED_NAV_WIDTH_PX - COLLAPSED_NAV_WIDTH_PX;
export const NAV_CONTENT_SHIFT_EASE = [0.16, 1, 0.3, 1] as const;
export const NAV_CONTENT_SHIFT_TRANSITION = {
    duration: NAV_WIDTH_TRANSITION_DURATION_MS / 1000,
    ease: NAV_CONTENT_SHIFT_EASE,
} as const;

export const getNavigationContentShiftPx = ({
    isHovered,
    isOpen,
    isOverlayOpen,
}: {
    readonly isHovered: boolean;
    readonly isOpen: boolean;
    readonly isOverlayOpen: boolean;
}): number => {
    return !isOpen && (isHovered || isOverlayOpen) ? NAV_HOVER_CONTENT_SHIFT_PX : 0;
};

type NavigationMenuContextValue = {
    readonly closeMenu: () => void;
    readonly isExpanded: boolean;
    readonly isHovered: boolean;
    readonly isOpen: boolean;
    readonly isOverlayOpen: boolean;
    readonly openMenu: () => void;
    readonly setOverlayOpen: (open: boolean) => void;
    readonly setHovered: (hovered: boolean) => void;
    readonly shouldContentOverflow: boolean;
    readonly toggleMenu: () => void;
};

const NavigationMenuContext = createContext<NavigationMenuContextValue | null>(null);

const STORAGE_KEY = 'navigation-menu-open';

const getStoredMenuState = (fallback = false): boolean => {
    try {
        const stored = globalThis.localStorage.getItem(STORAGE_KEY);

        if (stored === 'true') {
            return true;
        }

        return stored === 'false' ? false : fallback;
    } catch {
        return fallback;
    }
};

const saveMenuState = (isOpen: boolean): void => {
    try {
        globalThis.localStorage.setItem(STORAGE_KEY, String(isOpen));
    } catch {
        // localStorage not available, silently ignore
    }
};

const useOverflowLock = ({ isHovered, isOpen }: { readonly isHovered: boolean; readonly isOpen: boolean }): boolean => {
    const [isOverflowLockActive, setIsOverflowLockActive] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen) {
            setIsOverflowLockActive(false);
            return;
        }

        if (isHovered) {
            setIsOverflowLockActive(true);
        }
    }, [isHovered, isOpen]);

    useEffect(() => {
        if (isOpen || isHovered || !isOverflowLockActive) {
            return;
        }

        const timeoutId = globalThis.setTimeout(() => {
            setIsOverflowLockActive(false);
        }, NAV_WIDTH_TRANSITION_DURATION_MS);

        return () => {
            globalThis.clearTimeout(timeoutId);
        };
    }, [isOverflowLockActive, isHovered, isOpen]);

    return isOverflowLockActive;
};

export const NavigationMenuProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const {
        applicationState: { featureFlagService },
    } = useApplicationContext();
    const isWorkspaceNavigation = featureFlagService.isFeatureFlagEnabled('workspace-navigation');
    const [isOpen, setIsOpen] = useState<boolean>(() => getStoredMenuState(isWorkspaceNavigation));
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(false);
    const isOverflowLockActive = useOverflowLock({ isHovered, isOpen });

    const closeMenu = useCallback((): void => {
        setIsOpen(false);
        saveMenuState(false);
    }, []);

    const openMenu = useCallback((): void => {
        setIsOpen(true);
        saveMenuState(true);
    }, []);

    const toggleMenu = useCallback((): void => {
        setIsOpen((prev) => {
            const newState = !prev;
            saveMenuState(newState);

            return newState;
        });
    }, []);

    const setHovered = useCallback(
        (hovered: boolean): void => {
            setIsHovered(isWorkspaceNavigation ? false : hovered);
        },
        [isWorkspaceNavigation],
    );

    const setOverlayOpen = useCallback((open: boolean): void => {
        setIsOverlayOpen(open);
    }, []);

    const isExpanded = isOpen || (!isWorkspaceNavigation && (isHovered || isOverlayOpen));
    const shouldContentOverflow =
        !isWorkspaceNavigation && !isOpen && (isHovered || isOverlayOpen || isOverflowLockActive);

    return (
        <NavigationMenuContext.Provider
            value={{
                closeMenu,
                isExpanded,
                isHovered,
                isOpen,
                isOverlayOpen,
                openMenu,
                setOverlayOpen,
                setHovered,
                shouldContentOverflow,
                toggleMenu,
            }}
        >
            {children}
        </NavigationMenuContext.Provider>
    );
};

export const useNavigationMenu = (): NavigationMenuContextValue => {
    const context = useContext(NavigationMenuContext);

    if (context === null) {
        throw new Error('useNavigationMenu must be used within NavigationMenuProvider');
    }

    return context;
};
